import { highLevelApi } from '@sdk/main'
import type {
  AppsDbDeleteEntryPayload,
  AppsDbEntriesResult,
  AppsDbEntry,
  AppsDbGetEntriesPayload,
  AppsDbPsqlPayload,
  AppsDbUpsertEntryPayload,
} from '@sdk/payload.types'

const APPS_DB_BASE_PATH = '/core/api/protected/appsdb'

interface AppsDbContext {
  isIos: boolean
  origin: string
  accessToken: string
  instanceId?: string
}

async function fetchAppsDbContext(): Promise<AppsDbContext> {
  const env: any = await highLevelApi.API.request('get_env')
  const claimsDomain = env?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain
  const metadataDomain = env?.pitcher?.organization?.metadata?.custom_domain
  let origin = claimsDomain || metadataDomain || ''
  if (origin && !origin.startsWith('http')) origin = `https://${origin}`

  return {
    isIos: (env?.mode ?? env?.pitcher?.mode) === 'IOS',
    origin,
    accessToken: env?.pitcher?.access_token ?? '',
    instanceId: env?.pitcher?.instance?.id,
  }
}

// Hub flows chain SDK calls (psql search → PUT/POST), each needing the context;
// memoize the get_env round-trip so one logical operation doesn't re-marshal the
// full env over the bridge per call. Short TTL keeps a refreshed access token
// from going stale in the cache.
const CONTEXT_TTL_MS = 60_000
let contextCache: { promise: Promise<AppsDbContext>; fetchedAt: number } | null = null

function getAppsDbContext(): Promise<AppsDbContext> {
  if (contextCache && Date.now() - contextCache.fetchedAt < CONTEXT_TTL_MS) {
    return contextCache.promise
  }
  const promise = fetchAppsDbContext()
  contextCache = { promise, fetchedAt: Date.now() }
  promise.catch(() => {
    if (contextCache?.promise === promise) contextCache = null
  })
  return promise
}

async function appsDbRestFetch(
  ctx: AppsDbContext,
  path: string,
  init: { method: string; body?: object; params?: Record<string, any> },
): Promise<any> {
  // Orgs without a custom_domain resolve to an empty origin — fall back to the
  // current window origin (mirrors the old relative-fetch behavior, e.g. hub
  // dev behind the Vite proxy) instead of throwing TypeError: Invalid URL.
  const base = ctx.origin || (typeof window !== 'undefined' ? window.location.origin : '')
  const url = new URL(`${base}${APPS_DB_BASE_PATH}${path}`)
  Object.entries(init.params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value))
  })

  const response = await fetch(url.toString(), {
    method: init.method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${ctx.accessToken}`,
      ...(ctx.instanceId ? { 'x-instance-id': ctx.instanceId } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  })

  if (!response.ok) {
    let errorBody: any
    try {
      errorBody = await response.json()
    } catch {
      errorBody = undefined
    }
    const error = new Error(
      `AppsDB request failed: ${response.status}${errorBody?.error ? ` — ${errorBody.error}` : ''}`,
    )
    ;(error as Error & { status?: number }).status = response.status
    throw error
  }

  if (response.status === 204 || response.status === 205) return undefined
  // Guard against empty-body 2xx responses — response.json() would throw
  const text = await response.text()
  return text ? JSON.parse(text) : undefined
}

// Bridge rejections surface as the raw PITCHER_RESPONSE error body, e.g. iOS:
// { error_code: 'requestTypeDoesNotExists', localized_description: '...' }.
// With `casing: 'camel'` the keys arrive camelized, hence the errorCode check.
function hasErrorCode(error: any, code: string): boolean {
  return (
    error?.error_code === code ||
    error?.errorCode === code ||
    error?.code === code ||
    (typeof error?.reason === 'string' && error.reason.includes(code)) ||
    (typeof error?.message === 'string' && error.message.includes(code))
  )
}

function isTypeNotSyncedError(error: any): boolean {
  return hasErrorCode(error, 'type_not_synced')
}

/**
 * Version-skew guard: an app build whose native bridge predates the `appsdb_*`
 * handlers rejects them with `requestTypeDoesNotExists` — callers then use the
 * REST path, exactly as before this surface existed.
 */
function isBridgeUnsupportedError(error: any): boolean {
  return hasErrorCode(error, 'requestTypeDoesNotExists')
}

type PsqlCondition = { path: string; value: string | number | boolean | null }

function parsePsqlLiteral(raw: string): string | number | boolean | null | undefined {
  if (/^'.*'$/.test(raw)) return raw.slice(1, -1).replace(/''/g, "'")
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw)
  if (/^true$/i.test(raw)) return true
  if (/^false$/i.test(raw)) return false
  if (/^null$/i.test(raw)) return null
  return undefined
}

/** Blanks single-quoted literals ('' = escaped quote) so keyword checks only see text outside quotes. */
function stripQuotedLiterals(where: string): string {
  return where.replace(/'(?:[^']|'')*'/g, "''")
}

/** Splits a WHERE clause on top-level AND, respecting single-quoted literals. Null on unbalanced quotes. */
function splitTopLevelAnd(where: string): string[] | null {
  const parts: string[] = []
  let current = ''
  let inQuote = false
  let i = 0
  while (i < where.length) {
    const char = where[i]
    if (char === "'") {
      if (inQuote && where[i + 1] === "'") {
        current += "''"
        i += 2
        continue
      }
      inQuote = !inQuote
      current += char
      i += 1
      continue
    }
    if (!inQuote) {
      const separator = where.slice(i).match(/^ AND /i)
      if (separator) {
        parts.push(current)
        current = ''
        i += separator[0].length
        continue
      }
    }
    current += char
    i += 1
  }
  if (inQuote) return null
  parts.push(current)
  return parts
}

/**
 * Parses the subset of PSQL the local mirror can serve: `SELECT * FROM <type>`
 * with an optional AND-chain of `field = literal` conditions. Anything richer
 * (explicit field lists, OR/NOT, comparison operators, LIKE, ORDER BY, LIMIT)
 * returns null and the caller falls back to a network fetch. Quoted literals
 * are respected — a value containing e.g. ' AND ' or the word IN neither
 * mis-splits nor forces the network fallback.
 */
function parseSimplePsql(query: string): { type: string; conditions: PsqlCondition[] } | null {
  const normalized = query.replace(/\s+/g, ' ').trim()
  const match = normalized.match(/^SELECT \* FROM ([A-Za-z0-9_]+)( WHERE (.+))?$/i)
  if (!match) return null

  const type = match[1]
  const where = match[3]
  if (!where) return { type, conditions: [] }
  if (/\bOR\b|\bNOT\b|\bLIKE\b|\bIN\b|[<>]|!=/i.test(stripQuotedLiterals(where))) return null

  const rawConditions = splitTopLevelAnd(where)
  if (!rawConditions) return null

  const conditions: PsqlCondition[] = []
  for (const part of rawConditions) {
    const condition = part.trim().match(/^([A-Za-z0-9_.]+) ?= ?(.+)$/)
    if (!condition) return null
    const value = parsePsqlLiteral(condition[2].trim())
    if (value === undefined) return null
    conditions.push({ path: condition[1], value })
  }
  return { type, conditions }
}

function entryMatches(entry: AppsDbEntry, conditions: PsqlCondition[]): boolean {
  return conditions.every(({ path, value }) => {
    const actual = path.split('.').reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), entry)
    if (value === null) return actual === null || actual === undefined
    if (actual === null || actual === undefined) return false
    return String(actual) === String(value)
  })
}

/**
 * Lists AppsDB entries of a type, adapting to the platform:
 * - Web: GET to the next-core AppsDB REST API.
 * - iOS: reads the local offline mirror via the `appsdb_get_entries` bridge
 *   request. Types not allowlisted in the `offline_appsdb_types` setting fail
 *   with `code = type_not_synced` and automatically fall back to the network.
 *
 * @example
 * const { entries } = await api.appsDbGetEntries({ type: 'favorite' })
 */
export async function appsDbGetEntries(payload: AppsDbGetEntriesPayload): Promise<AppsDbEntriesResult> {
  if (!payload?.type || typeof payload.type !== 'string') {
    return Promise.reject(new Error('type is required and must be a non-empty string'))
  }

  const ctx = await getAppsDbContext()
  if (ctx.isIos) {
    try {
      return await highLevelApi.API.request('appsdb_get_entries', payload)
    } catch (error: any) {
      if (!isTypeNotSyncedError(error) && !isBridgeUnsupportedError(error)) throw error
    }
  }
  return appsDbRestFetch(ctx, '', { method: 'GET', params: payload })
}

/**
 * Creates or updates (deep-merges) an AppsDB entry, adapting to the platform:
 * - Web: POST (create) or PUT (update, when `id` is set) to the next-core AppsDB REST API.
 * - iOS: the `appsdb_upsert_entry` bridge request writes on the server and
 *   mirrors the authoritative result locally. Offline it rejects with
 *   `code = offline_write_not_supported` — AppsDB writes are online-only.
 *
 * The `data` blob is sent verbatim (no key-casing transformation), and all
 * `appsDb*` responses are likewise returned verbatim — even for
 * `casing: 'camel'` consumers — so bridge and REST results are identical.
 *
 * @example
 * const entry = await api.appsDbUpsertEntry({ type: 'favorite', user_id: 1, data: { file_id: 'abc' } })
 */
export async function appsDbUpsertEntry(payload: AppsDbUpsertEntryPayload): Promise<AppsDbEntry> {
  if (!payload?.data || typeof payload.data !== 'object') {
    return Promise.reject(new Error('data is required and must be an object'))
  }
  if (!payload.id && !payload.type) {
    return Promise.reject(new Error('type is required when creating an entry (no id provided)'))
  }

  const ctx = await getAppsDbContext()
  if (ctx.isIos) {
    try {
      return await highLevelApi.API.request('appsdb_upsert_entry', payload)
    } catch (error: any) {
      if (!isBridgeUnsupportedError(error)) throw error
    }
  }
  if (payload.id) {
    return appsDbRestFetch(ctx, `/${encodeURIComponent(payload.id)}`, { method: 'PUT', body: { data: payload.data } })
  }
  return appsDbRestFetch(ctx, '', { method: 'POST', body: payload })
}

/**
 * Deletes an AppsDB entry (soft delete server-side), adapting to the platform:
 * - Web: DELETE to the next-core AppsDB REST API.
 * - iOS: the `appsdb_delete_entry` bridge request deletes on the server and
 *   removes the entry from the local offline mirror. Offline it rejects with
 *   `code = offline_write_not_supported`.
 *
 * @example
 * await api.appsDbDeleteEntry({ id: '01H5ZXE7YP2JR6Q1Z2G3K4H5J6' })
 */
export async function appsDbDeleteEntry(payload: AppsDbDeleteEntryPayload): Promise<void> {
  if (!payload?.id || typeof payload.id !== 'string') {
    return Promise.reject(new Error('id is required and must be a non-empty string'))
  }

  const ctx = await getAppsDbContext()
  if (ctx.isIos) {
    try {
      return await highLevelApi.API.request('appsdb_delete_entry', payload)
    } catch (error: any) {
      if (!isBridgeUnsupportedError(error)) throw error
    }
  }
  await appsDbRestFetch(ctx, `/${encodeURIComponent(payload.id)}`, { method: 'DELETE' })
}

async function isDeviceOffline(): Promise<boolean> {
  try {
    return Boolean(await highLevelApi.API.request('is_offline'))
  } catch {
    // Fail open to the network — a broken connectivity probe must not route
    // reads through the possibly-stale mirror.
    return false
  }
}

// Mirror reads are windowed like the REST API — page in sync-sized chunks
// (mirrors the native sync page size). Reads are local-device SQLite, so
// there is no page-count cap: the loop drains the mirror completely.
const MIRROR_PAGE_LIMIT = 1000

/**
 * Serves a parsed psql query from the local offline mirror. Returns null when
 * the mirror can't serve it (type not in the `offline_appsdb_types` allowlist,
 * or a native bridge that predates the `appsdb_*` handlers) so the caller can
 * fall through to a network attempt.
 *
 * WHERE evaluation needs the full entry set, so windowed bridge responses are
 * paged until exhausted. Bridges without windowing support return everything
 * with no `hasMore`, ending the loop after one page.
 */
async function psqlFromMirror(parsed: {
  type: string
  conditions: PsqlCondition[]
}): Promise<AppsDbEntriesResult | null> {
  try {
    const entries: AppsDbEntry[] = []
    let hasMore = true
    while (hasMore) {
      const result: AppsDbEntriesResult = await highLevelApi.API.request('appsdb_get_entries', {
        type: parsed.type,
        limit: MIRROR_PAGE_LIMIT,
        offset: entries.length,
      })
      const page = result?.entries ?? []
      entries.push(...page)
      // An empty page can't advance the offset, so stop on it even if a
      // misbehaving bridge keeps reporting hasMore — otherwise the loop would
      // re-request the same offset forever (SDK requests have no timeout).
      hasMore = Boolean(result?.hasMore) && page.length > 0
    }
    const matched = entries.filter((entry) => entryMatches(entry, parsed.conditions))
    return { entries: matched, totalCount: matched.length, hasMore: false }
  } catch (error: any) {
    if (isTypeNotSyncedError(error) || isBridgeUnsupportedError(error)) return null
    throw error
  }
}

/**
 * Runs a PSQL (SQL-like) query against AppsDB: POST to the next-core
 * `/appsdb/psql` endpoint whenever the network is available.
 *
 * On iOS the local offline mirror (`appsdb_get_entries` + client-side
 * evaluation of simple `field = literal` AND-chains) serves the query ONLY
 * while the device is offline, or when an online fetch fails at the
 * connection level. Online queries are always answered by the server: Hub
 * flows use psql results to decide between creating and updating an entry,
 * and the mirror can lag the server by a sync cycle (multi-device) — a stale
 * empty read would turn an update into a duplicate create. Offline that same
 * stale read is harmless because AppsDB writes are rejected offline anyway.
 *
 * @example
 * const { entries } = await api.appsDbPsql({
 *   query: "SELECT * FROM personalfolders WHERE user_id = 1 AND data.custom_domain = 'acme.my.pitcher.com'",
 * })
 */
export async function appsDbPsql(payload: AppsDbPsqlPayload): Promise<AppsDbEntriesResult> {
  if (!payload?.query || typeof payload.query !== 'string') {
    return Promise.reject(new Error('query is required and must be a non-empty string'))
  }

  const ctx = await getAppsDbContext()
  if (ctx.isIos) {
    const parsed = parseSimplePsql(payload.query)
    let mirrorUnavailable = false
    if (parsed && (await isDeviceOffline())) {
      const local = await psqlFromMirror(parsed)
      if (local) return local
      mirrorUnavailable = true
    }
    try {
      return await appsDbRestFetch(ctx, '/psql', { method: 'POST', body: payload })
    } catch (error: any) {
      // fetch rejects with TypeError on connection-level failures (HTTP errors
      // carry a status) — the offline probe can race a real connectivity drop.
      // A mirror miss above can't succeed on retry, so don't probe it twice.
      if (parsed && !mirrorUnavailable && error instanceof TypeError) {
        const local = await psqlFromMirror(parsed)
        if (local) return local
      }
      throw error
    }
  }
  return appsDbRestFetch(ctx, '/psql', { method: 'POST', body: payload })
}
